import { Dialog } from '@base-ui/react/dialog'
import { cva, type VariantProps } from 'class-variance-authority'
import { MessageSquareWarning, ShieldAlert, X } from 'lucide-react'
import type { ReactNode } from 'react'

import { cn } from '../../lib/cn'
import { InsideModal, useReportSlot } from '../ReportProblem/report-slot'
import s from './Modal.module.css'

/**
 * La largeur de la carte est une **variante du composant**, pas une retouche depuis l'écran —
 * la règle que `Modal.module.css` annonçait à son bloc `.popup`, et celle que `Select` applique
 * déjà à son déclencheur : à spécificité égale, une classe d'écran qui redéfinirait
 * `inline-size` ne gagnerait que par l'ordre d'émission des feuilles.
 */
const popup = cva(s.popup, {
  variants: {
    width: {
      /** Les 560 px du prototype — un formulaire sur une colonne. */
      default: null,
      /** Deux colonnes côte à côte : un document à relire, et les champs qu'il a pré-remplis. */
      wide: s.wide,
      /** Les 720 px que le prototype passe à `openModal` pour la fenêtre « Fiche PDF ». */
      document: s.widthDocument,
      full: s.widthFull,
    },
    /**
     * L'habillage de la carte. `document` est la fenêtre « Fiche PDF » du prototype
     * (`pdfModal`) : en-tête sur la surface inverse, avec sous-titre et actions, et un corps
     * gris où le document se lit comme une feuille posée sur un bureau.
     */
    tone: {
      default: null,
      document: s.toneDocument,
    },
  },
  defaultVariants: { width: 'default', tone: 'default' },
})

/**
 * Modal — la fenêtre du prototype validé : voile bleu sombre, carte centrée de 560 px,
 * en-tête icône + titre + croix, pied d'actions.
 *
 * Le comportement vient de la primitive `Dialog` de Base UI (règle 3) : piège de focus,
 * fermeture à Échap et au clic sur le voile, verrou de défilement, rôles ARIA. L'apparence
 * est intégralement au module CSS, qui n'anime **ni l'ouverture ni la fermeture** — le
 * prototype fait paraître la fenêtre d'un coup, et le module dit pourquoi y revenir coûte cher.
 *
 * Le nom du composant est celui du prototype (`.modal`), pas celui de la primitive :
 * `Dialog` reste le nom de ce qu'il enveloppe, et confondre les deux rendrait chaque
 * import ambigu.
 *
 * **Contrôlée seulement** (`open` + `onOpenChange`) : c'est l'écran qui sait pourquoi elle
 * s'ouvre — la primitive offre un mode non contrôlé dont aucun usage n'existe ici. Aucun
 * déclencheur n'est exposé pour la même raison : il ferait une seconde source de vérité à
 * côté de l'état de l'écran.
 *
 * ⚠️ **Une boîte qui écrit passe `busy` et `error`** : fermée en route, sa requête partait quand
 * même et son refus ne s'affichait nulle part.
 */
export interface ModalProps extends VariantProps<typeof popup> {
  open: boolean
  onOpenChange: (open: boolean) => void
  /** L'icône d'en-tête du prototype, posée `aria-hidden` : le titre suffit au nom. */
  icon?: ReactNode
  title: ReactNode
  /** La ligne sous le titre — annoncée comme description du dialogue. */
  subtitle?: ReactNode
  /** Ce qui se range dans l'en-tête avant la croix : un badge, une action. */
  actions?: ReactNode
  children?: ReactNode
  /** Le pied d'actions. Absent, la carte se termine sur son contenu. */
  footer?: ReactNode
  /** Une écriture est en vol : rien ne ferme la boîte. Les actions du pied, l'écran les désarme. */
  busy?: boolean
  /** Le refus du serveur, mot pour mot. La boîte reste ouverte : la saisie se corrige. */
  error?: string | null
  /**
   * `false` retire le « Signaler un problème » de l'en-tête — la boîte du ticket elle-même. Sans
   * lui, une boîte ouverte cachait le seul bouton qui permettait de dire ce qui n'allait pas dedans.
   */
  reportable?: boolean
  className?: string
}

export function Modal({
  open,
  onOpenChange,
  icon,
  title,
  subtitle,
  actions,
  children,
  footer,
  busy,
  error,
  width,
  tone,
  reportable = true,
  className,
}: ModalProps) {
  const refused = error !== null && error !== undefined
  const report = useReportSlot()

  return (
    <Dialog.Root
      open={open}
      // Les détails restent ici : l'API de la primitive ne fuit pas. Pendant une écriture, la
      // fermeture est **annulée**, pas tue : la primitive ne joue alors rien de la sortie.
      onOpenChange={(next, details) => {
        if (!next && busy === true) details.cancel()
        else onOpenChange(next)
      }}
    >
      <Dialog.Portal>
        <Dialog.Backdrop className={s.backdrop} />
        <Dialog.Popup className={cn(popup({ width, tone }), className)}>
          <InsideModal.Provider value={true}>
            <header className={s.header}>
              {icon !== undefined && (
                <span className={s.icon} aria-hidden>
                  {icon}
                </span>
              )}
              {subtitle === undefined ? (
                <Dialog.Title className={s.title}>{title}</Dialog.Title>
              ) : (
                <div className={s.heading}>
                  <Dialog.Title className={s.title}>{title}</Dialog.Title>
                  <Dialog.Description className={s.subtitle}>{subtitle}</Dialog.Description>
                </div>
              )}
              {actions !== undefined && <div className={s.actions}>{actions}</div>}
              {/* Monté dans la carte, le ticket est une boîte imbriquée : celle-ci reste ouverte. */}
              {reportable &&
                report?.(({ onClick, disabled }) => (
                  <button
                    type="button"
                    className={cn(s.close, s.report)}
                    aria-label="Signaler un problème"
                    title="Signaler un problème"
                    disabled={disabled}
                    onClick={onClick}
                    data-report-trigger=""
                  >
                    <MessageSquareWarning size={18} aria-hidden />
                  </button>
                ))}
              {/* La croix du prototype — et l'issue tactile exigée par la primitive en modal. */}
              <Dialog.Close className={s.close} aria-label="Fermer" disabled={busy}>
                <X size={18} aria-hidden />
              </Dialog.Close>
            </header>
            <div className={s.body}>{children}</div>
            {(footer !== undefined || refused) && (
              <footer className={s.footer}>
                {refused && (
                  <p className={s.refusal} role="alert">
                    <ShieldAlert size={16} aria-hidden />
                    {error}
                  </p>
                )}
                {footer}
              </footer>
            )}
          </InsideModal.Provider>
        </Dialog.Popup>
      </Dialog.Portal>
    </Dialog.Root>
  )
}
